/**
 * Batch 10B — WebSocket client with auto-reconnect.
 *
 * Usage:
 *   import { wsClient } from "../lib/websocket";
 *   wsClient.connect(userId);
 *   wsClient.on("ticket_created", (data) => { ... });
 *   wsClient.disconnect();
 */

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000]; // exponential backoff
const PING_INTERVAL = 30000; // 30s keepalive

class WSClient {
    constructor() {
        this._ws = null;
        this._userId = null;
        this._listeners = {};
        this._reconnectAttempt = 0;
        this._reconnectTimer = null;
        this._pingTimer = null;
        this._intentionalClose = false;
    }

    /**
     * Connect to the WebSocket server.
     * @param {string} userId
     */
    connect(userId) {
        if (this._ws && this._ws.readyState <= 1) {
            // Already connected or connecting
            if (this._userId === userId) return;
            this.disconnect();
        }

        this._userId = userId;
        this._intentionalClose = false;
        this._doConnect();
    }

    _doConnect() {
        if (!this._userId) return;

        // Use current page origin for WS so custom domains work
        const origin = window.location.origin;
        const wsProtocol = origin.startsWith("https") ? "wss" : "ws";
        const host = origin.replace(/^https?:\/\//, "");
        const url = `${wsProtocol}://${host}/api/ws/${this._userId}`;

        try {
            this._ws = new WebSocket(url);
        } catch {
            this._scheduleReconnect();
            return;
        }

        this._ws.onopen = () => {
            this._reconnectAttempt = 0;
            this._startPing();
            this._emit("_connected", {});
        };

        this._ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.event === "pong") return;
                this._emit(msg.event, msg.data || {});
            } catch (e) {
                console.warn("WebSocket: non-JSON message ignored", e);
            }
        };

        this._ws.onclose = () => {
            this._stopPing();
            this._emit("_disconnected", {});
            if (!this._intentionalClose) {
                this._scheduleReconnect();
            }
        };

        this._ws.onerror = () => {
            // onclose will fire after onerror
        };
    }

    disconnect() {
        this._intentionalClose = true;
        this._stopPing();
        clearTimeout(this._reconnectTimer);
        if (this._ws) {
            this._ws.close();
            this._ws = null;
        }
        this._userId = null;
        this._reconnectAttempt = 0;
    }

    _scheduleReconnect() {
        clearTimeout(this._reconnectTimer);
        const delay = RECONNECT_DELAYS[Math.min(this._reconnectAttempt, RECONNECT_DELAYS.length - 1)];
        this._reconnectAttempt++;
        this._reconnectTimer = setTimeout(() => this._doConnect(), delay);
    }

    _startPing() {
        this._stopPing();
        this._pingTimer = setInterval(() => {
            if (this._ws && this._ws.readyState === WebSocket.OPEN) {
                this._ws.send("ping");
            }
        }, PING_INTERVAL);
    }

    _stopPing() {
        if (this._pingTimer) {
            clearInterval(this._pingTimer);
            this._pingTimer = null;
        }
    }

    /**
     * Register a listener for an event type.
     * @param {string} event - Event name (e.g., "ticket_created", "ticket_updated")
     * @param {Function} callback
     * @returns {Function} Unsubscribe function
     */
    on(event, callback) {
        if (!this._listeners[event]) this._listeners[event] = new Set();
        this._listeners[event].add(callback);
        return () => this._listeners[event]?.delete(callback);
    }

    _emit(event, data) {
        const cbs = this._listeners[event];
        if (cbs) {
            for (const cb of cbs) {
                try { cb(data); } catch (e) { console.warn("WebSocket listener error:", e); }
            }
        }
    }

    get connected() {
        return this._ws?.readyState === WebSocket.OPEN;
    }
}

// Singleton
export const wsClient = new WSClient();
